import { useCallback, useEffect, useRef, useState } from 'react'

import { recordEvent } from '../../observability/events'
import { SYNC_STATUS } from '../../redux/sync/constants'
import {
  readSyncStatusSnapshot,
  subscribePendingChanges,
} from '../../utils/syncStatusSource'

export const SYNC_STATUS_POLL_MS = 30000
export const SYNC_STATUS_MIN_GAP_MS = 5000
export const SYNC_STATUS_SLOW_READ_MS = 1500
// Junta as escritas de um mesmo salvar (registro + vínculos) numa recontagem só.
export const SYNC_STATUS_CHANGE_DEBOUNCE_MS = 300
// Mesmo valor de SYNC_COMPLETED_EVENT em TopActionBar.js (literal aqui para não criar
// import circular TopActionBar -> SyncIndicator -> hook; A5 tem um teste que garante a igualdade).
const SYNC_COMPLETED = 'progerar:sync-completed'
// Fases do download de fazendas (pullFarms), que grava em lote: escrita aqui não dispara leitura.
// O 1 ("Sincronizando") NÃO entra: o repository.js marca 1 em todo create/update/delete, e
// gatear por ele deixava o ✓ na tela com o registro recém-salvo sujo (QA HML U12, reteste).
const BULK_WRITE_STATES = [
  SYNC_STATUS.DOWNLOADING_FARM,
  SYNC_STATUS.PROCESSING,
  SYNC_STATUS.RETRYING,
]

/**
 * Lê o snapshot de status de envio: ao montar, a cada `intervalMs`, ao `currentSync` virar
 * 0/2 (ciclo terminou), em 'progerar:sync-completed', em online/offline, ao a aba voltar a
 * ficar visível e logo depois de uma escrita local nas tabelas enviadas (U12: sem isso o ✓
 * ficava na tela com o registro recém-salvo sujo). Nunca lê com a aba oculta, nunca 2 leituras
 * ao mesmo tempo, e ignora pedidos não forçados dentro de `minGapMs` (a contagem varre o Loki
 * — custo em aparelho fraco). Escritas durante o download de fazendas (BULK_WRITE_STATES: grava
 * em lote) não disparam leitura: só forçam a do fim do ciclo.
 */
export function useSyncStatus(currentSync, options = {}) {
  const {
    read = readSyncStatusSnapshot,
    intervalMs = SYNC_STATUS_POLL_MS,
    minGapMs = SYNC_STATUS_MIN_GAP_MS,
    subscribe = subscribePendingChanges,
  } = options

  const [snapshot, setSnapshot] = useState(null)
  const readRef = useRef(read)
  const gapRef = useRef(minGapMs)
  const alive = useRef(true)
  const inFlight = useRef(false)
  const lastRunAt = useRef(0)
  const slowReported = useRef(false)
  const changeTimer = useRef(null)
  const changedDuringSync = useRef(false)
  const syncRef = useRef(currentSync)
  const subscribeRef = useRef(subscribe)
  readRef.current = read
  gapRef.current = minGapMs
  syncRef.current = currentSync
  subscribeRef.current = subscribe

  const refresh = useCallback(async (force = false) => {
    if (inFlight.current) return
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    )
      return
    const startedAt = Date.now()
    if (!force && startedAt - lastRunAt.current < gapRef.current) return
    inFlight.current = true
    lastRunAt.current = startedAt
    try {
      const next = await readRef.current()
      if (!alive.current) return
      setSnapshot(next)
      if (next?.readMs > SYNC_STATUS_SLOW_READ_MS && !slowReported.current) {
        slowReported.current = true
        void recordEvent({
          level: 'info',
          category: 'sync',
          action: 'sync_status_poll_slow',
          payload: { readMs: next.readMs },
        })
      }
    } catch {
      if (alive.current) {
        setSnapshot({
          summary: null,
          lastPushOkAt: null,
          lastPullAt: null,
          online: true,
          readAt: Date.now(),
          readMs: 0,
        })
      }
    } finally {
      inFlight.current = false
    }
  }, [])

  useEffect(() => {
    alive.current = true
    refresh(true)
    const id = setInterval(() => refresh(), intervalMs)
    const force = () => refresh(true)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(true)
    }
    window.addEventListener(SYNC_COMPLETED, force)
    window.addEventListener('online', force)
    window.addEventListener('offline', force)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      alive.current = false
      clearInterval(id)
      window.removeEventListener(SYNC_COMPLETED, force)
      window.removeEventListener('online', force)
      window.removeEventListener('offline', force)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalMs, refresh])

  useEffect(() => {
    if (typeof subscribeRef.current !== 'function') return undefined
    const schedule = () => {
      if (changeTimer.current) return
      const sinceLast = Date.now() - lastRunAt.current
      const wait = Math.max(
        SYNC_STATUS_CHANGE_DEBOUNCE_MS,
        gapRef.current - sinceLast,
      )
      changeTimer.current = setTimeout(() => {
        changeTimer.current = null
        // Leitura em voo começou antes da escrita: tenta de novo depois dela.
        if (inFlight.current) schedule()
        else refresh(true)
      }, wait)
    }
    const onChange = () => {
      if (BULK_WRITE_STATES.includes(syncRef.current))
        changedDuringSync.current = true
      else schedule()
    }
    const unsubscribe = subscribeRef.current(onChange)
    return () => {
      clearTimeout(changeTimer.current)
      changeTimer.current = null
      if (typeof unsubscribe === 'function') unsubscribe()
    }
  }, [refresh])

  useEffect(() => {
    if (currentSync === 0 || currentSync === 2) {
      const force = changedDuringSync.current
      changedDuringSync.current = false
      refresh(force)
    }
  }, [currentSync, refresh])

  return { snapshot, refresh }
}
